// eslint-disable-next-line import/no-extraneous-dependencies
import { RiArrowDownSLine, RiSearchLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { Dropdown, Form, InputGroup } from "react-bootstrap";
import { DropDirection } from "react-bootstrap/esm/DropdownContext";
import { AiOutlineClose } from "react-icons/ai";
import {
    BiCheck, BiSearch, BiSquareRounded,
} from "react-icons/bi";
import { GrFormFilter } from "react-icons/gr";
import { createPortal } from "react-dom";
import { useTheme } from "../../contexts/Theme";
import DropdownList from "../theme/dropdown/Dropdown";
import FormGroup from "../theme/form-group/FormGroup";

export interface FilterPropGroup<T = never> {
    label: string | ReactElement;
    options: FilterPropGroupItem<T>[]
}

export interface FilterPropGroupItem<T = never> {
    value: string;
    text?: string;
    label: string | ReactElement;
    data?: T;
}

export interface FilterPropGroupDivider {
    isDivider: true;
}

export type FilterPropCategory<T = never> = FilterPropGroupItem<T> | FilterPropGroupDivider | FilterPropGroup<T>;

interface FilterProps<TIsMulti = true, TV = never> {
    categories?: FilterPropCategory<TV>[];
    selectedCategories?: TIsMulti extends true ? Set<string> : string;
    onSelected?: (ids: (TIsMulti extends true ? Set<string> : string)) => void;
    search?: string;
    onSetSearch?: (value: string) => void;
    onSearch?: (value: string) => Promise<FilterPropCategory<TV>[]>;
    isMulti?: TIsMulti;
    className?: string;
    toggleText?: string | ReactElement;
    togglePostfix?: string | ReactElement;
    toggleClassName?: string;
    canSelect?: boolean;
    inputClassName?: string;
    searchMinimum?: number;
    searchMaximum?: number;
    shouldReorderSelected?: boolean;
    icon?: ReactNode
    drop?: DropDirection;
    shouldPortal?: boolean;
    shouldDisplayAll?: boolean;
}

export function isDivider<TV>(item: FilterPropCategory<TV>): item is FilterPropGroupDivider {
    return Object.hasOwn(item, "isDivider");
}

export function isGroup<TV>(item: FilterPropCategory<TV>): item is FilterPropGroup<TV> {
    return Object.hasOwn(item, "options");
}

const MAX_SEARCH = 10;
export function Filters<TIsMulti extends boolean = true, TValue = never>({
    categories: categoriesList, selectedCategories, onSelected, search, onSetSearch,
    isMulti, className, toggleText, togglePostfix, toggleClassName, searchMaximum,
    canSelect, inputClassName, searchMinimum, onSearch, icon, shouldReorderSelected,
    drop, shouldPortal, shouldDisplayAll,

}: FilterProps<TIsMulti, TValue>): ReactElement | null {
    const [listSearch, setListSearch] = useState("");
    const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();

    const [categories, setCategories] = useState<FilterPropCategory<TValue>[]>(categoriesList ?? []);

    const theme = useTheme();
    useEffect(() => {
        if (categoriesList) {
            setCategories(categoriesList);
        }
    }, [categoriesList]);

    useEffect(() => {
        if (!onSearch) {
            return;
        }

        clearTimeout(searchTimer);
        setSearchTimer(setTimeout(async () => {
            const results = await onSearch(listSearch);
            setCategories(results);
        }, 500));
    }, [listSearch]);

    if (categories?.isEmpty && !onSetSearch) {
        return null;
    }

    const categoriesFlat = categories?.map(row => (isGroup(row) ? row.options : row)).flat();
    let results = categories ?? [];

    if (listSearch) {
        results = results?.mapFilter(row => {
            if (isDivider(row)) {
                return row;
            } if (isGroup(row)) {
                return {
                    label: row.label,
                    options: row.options.filter(child => (child.text ?? child.label as string)
                        .toLowerCase?.()
                        .includes(listSearch.toLowerCase())),
                };
            } if ((row.text ?? row.label as string).toLowerCase?.().includes(listSearch.toLowerCase())) {
                return row;
            }
            return undefined;
        });
    }
    const searchAmount = searchMaximum || MAX_SEARCH;
    const canSearch = onSearch || (searchMinimum && categoriesFlat && categoriesFlat?.length > searchMinimum);
    const hasMore = (canSearch && results?.length > searchAmount) || (searchMinimum && categoriesFlat.length > searchMinimum);
    if (canSearch && !shouldDisplayAll) {
        results = results?.limit(searchAmount);
    }

    const hasSelected = selectedCategories instanceof Set && selectedCategories?.size > 0;

    const renderItem = (item: FilterPropGroupItem<TValue>) => {
        const isSelected = canSelect && (
            ((selectedCategories instanceof Set)
        && selectedCategories.has(item.value)) || selectedCategories === item.value
        );
        return (
            <DropdownList.Item
                key={item.value}
                eventKey={item.value}
                isActive={theme.isV3 && isSelected}
                isChecked={theme.isV3 && canSelect && isSelected}
                onClick={() => {
                    if (isMulti) {
                        const selected = new Set(Array.from(selectedCategories as Set<string>)
                            .union(categoriesFlat?.mapFilter(
                                row => (!isDivider(row) ? row.value : undefined),
                            ) ?? []));
                        if (selected.has(item.value)) {
                            selected.delete(item.value);
                        } else {
                            selected.add(item.value);
                        }
                        const handler = onSelected as ((val: Set<string>) => void);
                        handler?.(selected);
                    } else {
                        (onSelected as ((val: string) => void))?.(item.value);
                    }
                }}
            >
                {!theme.isV3 && canSelect && (
                    isSelected
                        ? <BiCheck className="text-blue-dark me-1" />
                        : <BiSquareRounded className="text-blue-dark me-1" />
                )}
                <span>
                    {item.label}
                </span>
            </DropdownList.Item>
        );
    };

    const selectedSet = canSelect && (selectedCategories instanceof Set) ? selectedCategories : new Set(selectedCategories);

    let countSelected = 0;
    if (selectedCategories instanceof Set) {
        countSelected = selectedCategories.size;
    } else {
        countSelected = selectedCategories ? 1 : 0;
    }

    let menu = (
        <Dropdown.Menu className={classNames({
            "pt-0": !theme.isV3 && canSearch,
            "p3-1 w-100": theme.isV3,
        })}
        >
            {canSearch && !theme.isV3 && (
                <InputGroup className={theme.isV3 ? "border rounded p3-1" : "border"}>
                    <InputGroup.Text
                        id="search-categories"
                        className={theme.isV3 ? "" : "bg-transparent text-light border-0 pe-1"}
                    >
                        <BiSearch />
                    </InputGroup.Text>
                    <Form.Control
                        type="search"
                        placeholder="Search..."
                        className={`placeholder-light ${theme.isV3 ? "" : "p-2 mt-0 no-border "}`}
                        value={listSearch}
                        aria-label="Search"
                        aria-describedby="search-categories"
                        onChange={event => setListSearch(event.target.value)}
                    />
                </InputGroup>
            )}
            {canSearch && theme.isV3 && (
                <FormGroup
                    size="filter"
                    autoFocus
                    type="search"
                    prefixIcon={<RiSearchLine />}
                    placeholder="Search..."
                    aria-label="Search"
                    value={listSearch}
                    groupClassName="m3b-1"
                    onChange={event => setListSearch(event.target.value)}
                />

            )}
            {!theme.isV3 && ((results.isNotEmpty && hasSelected && isMulti) || hasMore) && (
                <div className="text-light mx-auto fst-italic w-100 text-center
                                mt-0 px-2 py-1 w-100 border-bottom d-flex gap-2 justify-content-between"
                >

                    {(results.isNotEmpty && hasSelected && isMulti) ? (
                        <button
                            type="button"
                            className="btn btn-none p-0 text-light"
                            onClick={() => {
                                const handler = onSelected as ((val: Set<string>) => void);
                                handler(new Set());
                            }}
                        >
                            <BiCheck className="text-light" />
                            {" "}
                            {selectedCategories.size}
                            {" "}
                            <AiOutlineClose className="text-light" />
                        </button>
                    ) : <div />}
                    {hasMore && (
                        <div>
                            {results.sum(row => (isGroup(row)
                                ? Math.min(row.options.length, searchMinimum ?? 0)
                                : 1))}
                            {" of "}
                            {categoriesFlat?.length}
                            {" "}
                            results.

                        </div>
                    )}
                </div>

            )}

            {listSearch && results.isEmpty && (
                <div className={
                    classNames("mx-auto fst-italic w-100 text-center mt-2", theme.isV3 ? "text-dim" : "text-light")
                }
                >
                    No results found.
                </div>
            )}

            {shouldReorderSelected && canSearch && results?.mapFilter(item => {
                if (isDivider(item)) {
                    return <Dropdown.Divider />;
                } if (isGroup(item)) {
                    const children = item.options.filter(child => selectedSet.has(child.value));
                    return children.isNotEmpty ? (
                        <div key={item.label as string}>
                            <div className="text-gray-600 mx-2 mt-2">{item.label}</div>
                            {(!shouldDisplayAll ? children.limit(searchMinimum ?? -1) : children).map(renderItem)}
                        </div>
                    ) : undefined;
                }
                return selectedSet.has(item.value) ? renderItem(item) : undefined;
            })}

            {results?.mapFilter((item, index) => {
                if (isDivider(item)) {
                    return <Dropdown.Divider />;
                } if (isGroup(item)) {
                    let children = item.options;
                    if (canSearch && shouldReorderSelected) {
                        children = children.filter(child => !selectedSet.has(child.value));
                    }
                    const isLast = index === results.length - 1;
                    return children.isNotEmpty ? (
                        <div key={item.label as string}>
                            {theme.isV3 ? (
                                <DropdownList.Header>{item.label}</DropdownList.Header>
                            ) : (
                                <div className="text-gray-600 mx-2 mt-2">{item.label}</div>
                            )}
                            {(!shouldDisplayAll ? children.limit(searchMinimum ?? -1) : children).map(renderItem)}
                            {theme.isV3 && !isLast && (
                                <DropdownList.Divider />
                            )}
                        </div>
                    ) : undefined;
                }
                return (
                    !canSearch || !shouldReorderSelected || !selectedSet.has(item.value))
                    ? renderItem(item)
                    : undefined;
            })}

        </Dropdown.Menu>
    );

    if (shouldPortal) {
        menu = createPortal(menu, document.body);
    }

    return (
        <div className={`d-flex align-items-center ${className}`}>
            {categories?.isNotEmpty && (
                <Dropdown
                    drop={drop}
                    autoClose="outside"
                    className={!onSetSearch ? "w-100" : ""}
                >
                    {theme.isV3 ? (
                        <DropdownList.Toggle
                            variant="filter"
                            prefixIcon={icon}
                            className="p3x-2 w-100 text-start d-flex align-items-center"
                            postfixIcon={<RiArrowDownSLine />}
                            isActive={countSelected > 0}
                        >
                            <div className="w-100 d-inline">
                                <span>
                                    {toggleText}
                                    :
                                    {" "}
                                </span>
                                {countSelected > 0 && (
                                    <>
                                        {countSelected}
                                        {" "}
                                        selected
                                    </>
                                )}
                                {togglePostfix}

                            </div>

                        </DropdownList.Toggle>
                    ) : (
                        <Dropdown.Toggle
                            variant=""
                            className={toggleText ? toggleClassName : "menu-small p-1"}
                        >
                            {toggleText ? (
                                <>
                                    {toggleText}
                                    {" "}
                                    {selectedCategories instanceof Set
                                && selectedCategories?.size > 0
                                && `${selectedCategories.size} selected`}
                                </>
                            ) : <GrFormFilter />}
                        </Dropdown.Toggle>
                    )}
                    {menu}
                </Dropdown>
            )}
            {onSetSearch && (
                <input
                    className={`no-border ms-2 ${inputClassName}`}
                    type="search"
                    placeholder="Search..."
                    value={search}
                    onChange={evt => onSetSearch(evt.target.value)}
                />
            )}

        </div>
    );
}

Filters.defaultProps = {
    categories: [],
    selectedCategories: undefined,
    onSelected: undefined,
    isMulti: true,
    onSetSearch: undefined,
    search: "",
    className: "mb-1",
    toggleText: undefined,
    togglePostfix: undefined,
    toggleClassName: "",
    canSelect: true,
    inputClassName: "",
    searchMinimum: 10,
    searchMaximum: MAX_SEARCH,
    onSearch: undefined,
    icon: undefined,
    shouldReorderSelected: true,
    drop: undefined,
    shouldPortal: false,
    shouldDisplayAll: false,
};

export type { DropDirection };
